import { cn } from "@/lib/utils";
import * as React from "react";

interface LoaderProps {
  size?: number;
  text?: string;
  className?: string;
  overlay?: boolean;
}

export const Component: React.FC<LoaderProps> = ({
  size = 180,
  text = "Generating",
  className,
  overlay = true,
}) => {
  const letters = text.split("");

  const fontSize = Math.max(6, Math.round(size / 6));
  const loaderStyles = React.useMemo<React.CSSProperties>(
    () => ({
      width: size,
      height: size,
      fontSize,
      "--loader-scale": Math.max(size / 120, 0.2),
    }),
    [size, fontSize],
  );

  return (
    <div
      className={cn(
        overlay
          ? "fixed inset-0 z-50 flex items-center justify-center"
          : "relative inline-flex items-center justify-center",
        className,
      )}
    >
      <div
        className="relative bg-black dark:bg-white rounded-full flex items-center justify-center font-inter select-none"
        style={loaderStyles}
      >
        {letters.map((letter, index) => (
          <span
            key={index}
            className="inline-block text-white dark:text-gray-800 opacity-40 animate-loaderLetter"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {letter}
          </span>
        ))}

        <div
          className="absolute inset-0 rounded-full animate-loaderCircle"
        ></div>
      </div>

      <style jsx>{`
        @keyframes loaderCircle {
          0% {
            transform: rotate(90deg);
            box-shadow:
              0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 12px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 12px) calc(var(--loader-scale, 1) * 18px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 36px) calc(var(--loader-scale, 1) * 36px) 0 #e879f9 inset,
              0 0 calc(var(--loader-scale, 1) * 3px) calc(var(--loader-scale, 1) * 1.2px) rgba(232, 121, 249, 0.3),
              0 0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 1.8px) rgba(232, 121, 249, 0.2);
          }
          50% {
            transform: rotate(270deg);
            box-shadow:
              0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 12px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 12px) calc(var(--loader-scale, 1) * 6px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 24px) calc(var(--loader-scale, 1) * 36px) 0 #e879f9 inset,
              0 0 calc(var(--loader-scale, 1) * 3px) calc(var(--loader-scale, 1) * 1.2px) rgba(232, 121, 249, 0.3),
              0 0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 1.8px) rgba(232, 121, 249, 0.2);
          }
          100% {
            transform: rotate(450deg);
            box-shadow:
              0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 12px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 12px) calc(var(--loader-scale, 1) * 18px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 36px) calc(var(--loader-scale, 1) * 36px) 0 #e879f9 inset,
              0 0 calc(var(--loader-scale, 1) * 3px) calc(var(--loader-scale, 1) * 1.2px) rgba(232, 121, 249, 0.3),
              0 0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 1.8px) rgba(232, 121, 249, 0.2);
          }
        }

        @keyframes loaderLetter {
          0%,
          100% {
            opacity: 0.4;
            transform: translateY(0);
          }
          20% {
            opacity: 1;
            transform: scale(1.15);
          }
          40% {
            opacity: 0.7;
            transform: translateY(0);
          }
        }

        .animate-loaderCircle {
          animation: loaderCircle 5s linear infinite;
        }

        .animate-loaderLetter {
          animation: loaderLetter 3s infinite;
        }

       
        @media (prefers-color-scheme: dark) {
          .animate-loaderCircle {
            box-shadow:
              0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 12px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 12px) calc(var(--loader-scale, 1) * 18px) 0 #e879f9 inset,
              0 calc(var(--loader-scale, 1) * 36px) calc(var(--loader-scale, 1) * 36px) 0 #e879f9 inset,
              0 0 calc(var(--loader-scale, 1) * 3px) calc(var(--loader-scale, 1) * 1.2px) rgba(232, 121, 249, 0.3),
              0 0 calc(var(--loader-scale, 1) * 6px) calc(var(--loader-scale, 1) * 1.8px) rgba(232, 121, 249, 0.2);
          }
        }
      `}</style>
    </div>
  );
};
